import QRCode from "qrcode";
import { barcodeSvgInnerForExport } from "./barcodeQr";
import type { DocV1, NodeAny, NodeBarcode, NodeIcon, NodeImage, NodeQrcode, NodeText } from "./types";
import { iconToSvg } from "./icons";

function esc(s: string) {
  return s
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function nodeToSvg(node: Exclude<NodeAny, NodeQrcode>): string {
  const tr = `translate(${node.x} ${node.y}) rotate(${node.rotation})`;
  const opacity = node.opacity ?? 1;

  switch (node.type) {
    case "text": {
      const n = node as NodeText;
      // No double quotes inside style="..." — that breaks XML. Use single-quoted family names in CSS.
      const style = `font-family: ${esc(n.fontFamily)}, system-ui, -apple-system, sans-serif; font-size: ${n.fontSize}px; font-style: ${n.fontStyle}; font-weight: ${n.fontWeight}; fill: ${n.fill};`;
      const anchor = n.align === "center" ? "middle" : n.align === "right" ? "end" : "start";
      const x =
        n.align === "center" ? n.width / 2 : n.align === "right" ? n.width : 0;
      const lines = n.text.split("\n");
      const lineHeight = n.fontSize * 1.2;
      const text = lines
        .map((line, i) => {
          const dy = i === 0 ? 0 : lineHeight;
          return `<tspan x="${x}" dy="${dy}">${esc(line)}</tspan>`;
        })
        .join("");
      return `<g transform="${tr}" opacity="${opacity}"><text xml:space="preserve" text-anchor="${anchor}" style="${style}">${text}</text></g>`;
    }
    case "image": {
      const n = node as NodeImage;
      return `<g transform="${tr}" opacity="${opacity}"><image href="${esc(
        n.srcDataUrl,
      )}" width="${n.w}" height="${n.h}" preserveAspectRatio="none" /></g>`;
    }
    case "line": {
      const p = node.points;
      const d: string[] = [];
      for (let i = 0; i < p.length; i += 2) {
        d.push(`${i === 0 ? "M" : "L"} ${p[i]} ${p[i + 1]}`);
      }
      return `<g transform="${tr}" opacity="${opacity}"><path d="${d.join(
        " ",
      )}" fill="none" stroke="${node.stroke}" stroke-width="${node.strokeWidth}" stroke-linecap="round" stroke-linejoin="round"/></g>`;
    }
    case "rect": {
      return `<g transform="${tr}" opacity="${opacity}"><rect width="${node.w}" height="${node.h}" rx="${node.cornerRadius}" fill="${node.fill}" stroke="${node.stroke}" stroke-width="${node.strokeWidth}"/></g>`;
    }
    case "circle": {
      return `<g transform="${tr}" opacity="${opacity}"><circle cx="${node.r}" cy="${node.r}" r="${node.r}" fill="${node.fill}" stroke="${node.stroke}" stroke-width="${node.strokeWidth}"/></g>`;
    }
    case "barcode": {
      const n = node as NodeBarcode;
      return `<g transform="${tr}" opacity="${opacity}">${barcodeSvgInnerForExport(n)}</g>`;
    }
    case "icon": {
      const n = node as NodeIcon;
      return `<g transform="${tr}" opacity="${opacity}">${iconToSvg(
        n.icon,
        n.w,
        n.h,
        n.fill,
      )}</g>`;
    }
  }
}

async function qrcodeNodeToSvg(n: NodeQrcode): Promise<string> {
  const tr = `translate(${n.x} ${n.y}) rotate(${n.rotation})`;
  const opacity = n.opacity ?? 1;
  const light =
    n.background === "transparent" || n.background === "rgba(0,0,0,0)"
      ? "#ffffff"
      : n.background;
  const dataUrl = await QRCode.toDataURL(n.value.trim() || " ", {
    width: Math.max(64, Math.round(Math.max(n.w, n.h) * 2)),
    margin: 1,
    color: { dark: n.foreground, light },
    errorCorrectionLevel: "M",
  });
  return `<g transform="${tr}" opacity="${opacity}"><image href="${esc(dataUrl)}" width="${n.w}" height="${n.h}" preserveAspectRatio="none" /></g>`;
}

export type LabelRectPx = { x: number; y: number; w: number; h: number };

/**
 * Vector export: user space = millimetres (matches width/height on the root and doc.sizeMM).
 * Stage node coords are in px; group scale s = wMm/label.w maps px → mm (same as editor 8px/mm).
 */
export async function exportDocToSvg(
  doc: DocV1,
  label: LabelRectPx,
): Promise<{ svg: string; widthMM: number; heightMM: number }> {
  const wMm = doc.sizeMM.w;
  const hMm = doc.sizeMM.h;
  if (!(label.w > 0) || !(label.h > 0) || !(wMm > 0) || !(hMm > 0)) {
    throw new Error("Некорректный размер наклейки для экспорта");
  }
  const s = wMm / label.w;
  const e = -label.x * s;
  const f = -label.y * s;

  const parts = await Promise.all(
    doc.nodes.map(async (n) => {
      if (n.type === "qrcode") return qrcodeNodeToSvg(n);
      return nodeToSvg(n);
    }),
  );
  const nodes = parts.join("");

  const wStr = String(wMm);
  const hStr = String(hMm);
  const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${wStr}mm" height="${hStr}mm" viewBox="0 0 ${wStr} ${hStr}" overflow="hidden">
  <rect x="0" y="0" width="${wStr}" height="${hStr}" fill="white" />
  <g transform="matrix(${s} 0 0 ${s} ${e} ${f})">
  ${nodes}
  </g>
</svg>`;
  return { svg, widthMM: wMm, heightMM: hMm };
}
